/**
 * Map product-master API error keys to frontend i18n strings.
 * Backend returns message keys like "error.product_master.name_taken".
 */

type TFunction = (key: string, options?: Record<string, unknown>) => string;

const KEY_MAP: Record<string, string> = {
	"error.product_master.name_taken": "productCatalog.errors.nameTaken",
	"error.product_master.code_taken": "productCatalog.errors.codeTaken",
	"error.product_master.barcode_in_use": "productCatalog.errors.barcodeInUse",
	"error.product_master.barcode_conflicts_item_code":
		"productCatalog.errors.barcodeConflictsItemCode",
	"error.product_master.invalid_category": "productCatalog.errors.invalidCategory",
	"error.product_master.invalid_family": "productCatalog.errors.invalidFamily",
	"error.product_master.invalid_sector": "productCatalog.errors.invalidSector",
	"error.product_master.integrity": "productCatalog.errors.integrity",
	"error.product_master.in_use": "productCatalog.errors.inUse",
	"error.product_master.missing_item_id": "productCatalog.errors.generic",
	"error.product_master.flush_failed": "productCatalog.errors.generic",
	"error.validation.failed": "productCatalog.errors.validationFailed",
	"error.record.not_found": "productCatalog.errors.notFound",
	"error.perm.create_records": "productCatalog.errors.generic",
	"error.perm.update_records": "productCatalog.errors.generic",
	"error.perm.delete_records": "productCatalog.errors.generic",
	"error.perm.access_items": "productCatalog.errors.generic",
};

/** Extract API error payload from axios error or response-like object. */
export function extractApiErrorPayload(err: unknown): {
	message?: string;
	barcode?: string;
	status?: number;
} {
	const anyErr = err as {
		response?: { status?: number; data?: Record<string, unknown> };
		data?: Record<string, unknown>;
		status?: number;
		message?: string;
	};

	const data = anyErr?.response?.data ?? anyErr?.data;
	const status = anyErr?.response?.status ?? anyErr?.status;

	if (data && typeof data === "object") {
		const message = typeof data.message === "string" ? data.message : undefined;
		const barcode = typeof data.barcode === "string" ? data.barcode : undefined;
		return { message, barcode, status };
	}

	if (typeof anyErr?.message === "string") {
		return { message: anyErr.message, status };
	}

	return { status };
}

/**
 * Translate a product-master API error into a user-facing string.
 * Falls back to `fallbackKey` under productCatalog (or full key) when unmapped.
 */
export function translateProductMasterError(
	t: TFunction,
	err: unknown,
	fallbackKey: string,
): string {
	const { message, barcode, status } = extractApiErrorPayload(err);

	if (status === 409 && message === "error.product_master.in_use") {
		return t("productCatalog.errors.inUse");
	}

	if (message && KEY_MAP[message]) {
		return t(KEY_MAP[message], barcode ? { barcode } : undefined);
	}

	// Already a human message from older paths — avoid showing raw keys
	if (message && !message.startsWith("error.") && message.length < 200) {
		return message;
	}

	if (fallbackKey.startsWith("productCatalog.")) {
		return t(fallbackKey);
	}
	return t(fallbackKey);
}
